import { GridCellProps } from '@progress/kendo-react-grid';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import GridCellBase from 'src/components/GridCellBase';
import { CanAccessReceipts, CreateInvoicePermission } from 'src/components/guards/PermissionsList';
import usePermission from 'src/hooks/usePermissions';
import useRedirect from 'src/hooks/useRedirect';

const DocumentNumberCell = (props: GridCellProps) => {
  const { dataItem } = props;
  const { documentType, buyerId } = dataItem;
  const [searchParams] = useSearchParams();
  const { redirect, getUrl } = useRedirect();
  const receiptAccess = usePermission({
    permissions: CanAccessReceipts,
    or: true,
    clientId: buyerId,
  });
  const invoiceAccess = usePermission({
    permissions: CreateInvoicePermission,
    or: true,
    clientId: buyerId,
  });

  const receiptId = useMemo(() => searchParams.get('recid'), [searchParams]);
  const isCurrent = useMemo(
    () => dataItem?.documentId?.toString() === receiptId,
    [dataItem?.documentId, receiptId]
  );

  const linkEnabled = useMemo(
    () => (documentType === 'Receipt' ? receiptAccess : invoiceAccess),
    [documentType, invoiceAccess, receiptAccess]
  );

  const handleClick = useCallback(() => {
    if (!isCurrent && linkEnabled) {
      redirect(
        getUrl(dataItem?.documentType === 'Receipt' ? 'receiptDetails' : 'invoiceDetails')(
          dataItem.documentId,
          dataItem.poId
        )
      );
    }
  }, [
    isCurrent,
    linkEnabled,
    redirect,
    getUrl,
    dataItem?.documentType,
    dataItem.documentId,
    dataItem.poId,
  ]);

  const dataValue = `${dataItem?.documentNumber || 'Unassigned'}${isCurrent ? ' (Current)' : ''}`;

  return (
    <GridCellBase title={dataValue} {...props} rowSelectionEnabled={true}>
      <button
        onClick={handleClick}
        className={
          linkEnabled
            ? !isCurrent
              ? 'text-selected cursor-pointer underline'
              : 'cursor-default'
            : 'cursor-default'
        }
      >
        {dataValue}
      </button>
    </GridCellBase>
  );
};

export default DocumentNumberCell;
